<template>
  <div>
    <div class="text-center mb-8">
      <div class="text-[48px]">💌</div>
      <div class="text-[#161616] text-[28px] font-bold leading-9 mb-2 lg:mb-4">
        Votre prochaine mission de bénévolat dans votre boîte mail
      </div>
      <div class="text-[#666666] lg:text-lg max-w-md mx-auto">
        <strong>Recevez un e-mail par semaine</strong> avec les nouvelles missions disponibles.
      </div>
    </div>
    <div class="mx-auto">
      <form id="form" class="space-y-8" @submit.prevent="onSubmit">
        <DsfrFormControl
          html-for="type_missions"
          :error="errors.type_missions"
          class="mt-6 @container"
          label="Type de missions"
          required
        >
          <DsfrTagsGroup
            v-model="form.type_missions"
            name="type_missions"
            :options="[
              { key: 'presentiel', label: 'Sur place' },
              { key: 'distance', label: 'À distance' },
              { key: 'all', label: 'Les deux' },
            ]"
            context="radio"
          />
        </DsfrFormControl>

        <div
          v-if="['presentiel', 'all'].includes(form.type_missions)"
          class="grid grid-cols-1 lg:grid-cols-3 gap-4"
        >
          <DsfrFormControl
            html-for="zip"
            :error="errors.zip"
            class="lg:col-span-2 @container"
            label="Ville"
            required
          >
            <DsfrInputAutocomplete
              :model-value="form.zip ? `${form.zip} - ${form.city}` : null"
              name="zip"
              :options="zipAutocompleteOptions"
              :min-length-to-search="3"
              option-key-attribute="id"
              option-label-attribute="label"
              option-label-secondary-attribute="typeLabel"
              placeholder="Rechercher une ville ou code postal"
              search-input-placeholder="Recherche par ville ou code postal"
              :loading="loadingFetchZips"
              @selected="handleSelectedZip"
              @fetch-suggestions="onFetchZipSuggestions($event)"
            />
          </DsfrFormControl>
          <DsfrFormControl
            html-for="radius"
            :error="errors.radius"
            class="@container"
            label="Rayon"
            required
          >
            <DsfrSelect
              selectId="radius"
              v-model="form.radius"
              :options="[
                { key: 10000, label: '10 km' },
                { key: 25000, label: '25 km' },
                { key: 50000, label: '50 km' },
              ]"
            />
          </DsfrFormControl>
        </div>

        <DsfrFormControl html-for="activities" :error="errors.activities" label="Activités">
          <DsfrSelectMultiple
            v-model="form.activities"
            selectId="activities"
            :options="activities"
          />
        </DsfrFormControl>
        <DsfrFormControl html-for="commitment" :error="errors.commitment" label="Disponibilités">
          <DsfrSelect
            v-model="form.commitment"
            selectId="commitment"
            :options="$labels.commitment"
          />
        </DsfrFormControl>

        <div class="">
          <DsfrButton :loading="loading" class="w-full" full @click.native.prevent="onSubmit">
            {{ isModeUpdating ? 'Enregistrer' : 'Créer mon alerte' }}
          </DsfrButton>
          <div class="text-center text-[#666666] text-sm mt-4">
            Vous pourrez la désactiver à tout moment.
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { string, object, array, number } from 'yup'
import FormErrors from '@/mixins/form/errors'
import activities from '@/assets/activities.json'
import GeolocProfile from '@/mixins/geoloc-profile'

export default defineNuxtComponent({
  name: 'UserAlertStepForm',
  props: {
    initialForm: {
      type: Object,
      required: true,
    },
  },
  mixins: [FormErrors, GeolocProfile],
  emits: ['next'],
  data() {
    return {
      form: _cloneDeep(this.initialForm),
      activities: activities,
      loading: false,
      zipAutocompleteOptions: [],
      loadingFetchZips: false,
      formSchema: object({
        type_missions: string().required('Un type de mission est requis'),
        activities: array().min(1, 'Une activité est requise').required('Une activité est requise'),
        commitment: string().required('Une disponibilité est requise'),
        zip: string()
          .nullable()
          .when('type_missions', {
            is: (type_missions) => type_missions !== 'distance',
            then: (schema) => schema.required('Un code postal est requis'),
          }),
        city: string()
          .nullable()
          .when('type_missions', {
            is: (type_missions) => type_missions !== 'distance',
            then: (schema) => schema.required('Une ville est requise'),
          }),
        latitude: string()
          .nullable()
          .when('type_missions', {
            is: (type_missions) => type_missions !== 'distance',
            then: (schema) => schema.required('Une latitude est requise'),
          }),
        longitude: string()
          .nullable()
          .when('type_missions', {
            is: (type_missions) => type_missions !== 'distance',
            then: (schema) => schema.required('Une longitude est requise'),
          }),
        radius: number().when('type_missions', {
          is: (type_missions) => type_missions !== 'distance',
          then: (schema) => schema.required('Un rayon est requis'),
        }),
      }),
    }
  },
  computed: {
    isModeUpdating() {
      return this.$stores.userAlert.selectedAlert !== null
    },
  },
  methods: {
    async createAlert() {
      await apiFetch('/user/alerts', {
        method: 'POST',
        body: this.form,
      })

      this.$plausible.trackEvent('User Alert - Creation')

      this.$toast.success('Votre alerte a bien été créée.')
    },
    async updateAlert() {
      await apiFetch(`/user/alerts/${this.$stores.userAlert.selectedAlert.id}`, {
        method: 'PUT',
        body: this.form,
      })

      this.$toast.success('Votre alerte a bien été modifiée.')
    },
    onSubmit() {
      if (this.loading) {
        return
      }
      this.loading = true
      this.formSchema
        .validate(this.form, { abortEarly: false })
        .then(async () => {
          if (this.$stores.userAlert.selectedAlert) {
            await this.updateAlert()
          } else {
            await this.createAlert()
          }

          await this.$stores.userAlert.fetch()
          this.$emit('next')
        })
        .catch((errors) => {
          this.setErrors(errors)
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
})
</script>
